import {SelectPickerDialog} from '@github-ui/filter-picker'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {Button, NavList} from '@primer/react'
import styles from './styles.module.css'

import {useMutation} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useState} from 'react'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import type {DynamicSidePanelItem} from './DynamicSidePanelItems'
import {createItemConfig} from '../../utils/item-config'

interface SidePanelItemsListProps {
  items: DynamicSidePanelItem[]
  onItemClick: () => void
}

function SidePanelItemsList({items, onItemClick}: SidePanelItemsListProps) {
  return (
    <>
      {items.flat().map(item => (
        <NavList.Item key={item.value} href={item.url} onClick={onItemClick}>
          <NavList.LeadingVisual>
            <GitHubAvatar size={16} src={item.avatar_url} />
          </NavList.LeadingVisual>
          {item.value}
        </NavList.Item>
      ))}
    </>
  )
}

export interface PinnableDynamicSidePanelProps {
  /** The items to show in the list. */
  items: DynamicSidePanelItem[]

  /** Title for the full section */
  title: string

  /** Label for the picker */
  pickerLabel: string

  /** URL to pin/unpin items */
  pinUrl: string

  /** URL to search for items */
  searchUrl: string

  /** Action to send for analytics actions */
  analytics: {
    /** Optional sub-category to send with analytics events */
    subCategory?: string

    /** Action to send for users interacting w/ an individual item */
    itemAction: string
  }
}

export function PinnableDynamicSidePanelItems(props: PinnableDynamicSidePanelProps) {
  const {items, title, analytics, pickerLabel, pinUrl, searchUrl} = props
  const {sendItemClick} = useGlobalNavAnalytics()

  const [showPicker, setShowPicker] = useState(false)
  const [pinnedItems, setPinnedItems] = useState<DynamicSidePanelItem[]>(items)
  const updatePinsMutation = usePinUnpinItem(pinUrl)
  const lowerCaseTitle = title.toLowerCase()
  const itemConfig = createItemConfig(title, searchUrl)

  const handlePickerSubmit = (selected: DynamicSidePanelItem[]) => {
    setPinnedItems(selected)
    updatePinsMutation.mutate(selected)
  }

  const renderPickerDialog = () => {
    return (
      <SelectPickerDialog<DynamicSidePanelItem>
        title={`${pickerLabel} ${lowerCaseTitle}`}
        selectionVariant="multiple"
        onSubmit={handlePickerSubmit}
        selected={pinnedItems}
        onDismiss={() => setShowPicker(false)}
        itemConfig={itemConfig}
        providers={[]}
        data-testid="dynamic-side-panel-items-picker-dialog"
      />
    )
  }

  const renderEmptyState = () => (
    <NavList.Group>
      <NavList.GroupHeading as="h3" className={styles.groupHeading}>
        <div>{title}</div>
      </NavList.GroupHeading>
      <NavList.Item>
        <div className="border border-dashed rounded p-3 mt-2">
          <span className="mb-3">Add repositories you&apos;ve worked on here for quick access.</span>
          <Button
            aria-label={`Add ${lowerCaseTitle}`}
            data-testid="dynamic-side-panel-items-add-items-button"
            onClick={() => setShowPicker(true)}
            className="mt-2"
          >
            Add {lowerCaseTitle}
          </Button>
        </div>
      </NavList.Item>
    </NavList.Group>
  )

  const renderItemsList = () => (
    <NavList.Group>
      <NavList.GroupHeading as="h3" className={styles.groupHeading}>
        <div>{title}</div>
        <Button
          size="small"
          variant="invisible"
          aria-label={`${pickerLabel} ${lowerCaseTitle}`}
          data-testid="dynamic-side-panel-items-picker-button"
          onClick={() => setShowPicker(true)}
        >
          {pickerLabel}
        </Button>
      </NavList.GroupHeading>

      <SidePanelItemsList items={pinnedItems} onItemClick={() => sendItemClick(analytics.itemAction)} />
    </NavList.Group>
  )

  return (
    <>
      {pinnedItems.length === 0 ? renderEmptyState() : renderItemsList()}
      {showPicker && renderPickerDialog()}
    </>
  )
}

export interface PinnableDynamicMenuItemsProps {
  initialItems: DynamicSidePanelItem[]
  pinUrl: string
  searchUrl: string
}

export function usePinUnpinItem(pinUrl: string) {
  const mutation = useMutation({
    mutationFn: async (items: DynamicSidePanelItem[]) => {
      if (pinUrl) {
        await reactFetchJSON(pinUrl, {
          method: 'POST',
          body: {
            pinned_items_id_and_type: items.map(item => `${item.id}-Repository`),
          },
        })
      }
    },
  })

  return mutation
}

try{ SidePanelItemsList.displayName ||= 'SidePanelItemsList' } catch {}
try{ PinnableDynamicSidePanelItems.displayName ||= 'PinnableDynamicSidePanelItems' } catch {}